// @ts-nocheck — verbatim port from xop-edge-staff. See src/crm/README.md.
import { useState, useEffect } from "react"
import { useParams, useNavigate } from "react-router-dom";
import { useToast } from "@crm-src/hooks/use-toast"
import {
  Avatar,
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  Divider,
  ListSubheader,
  Menu,
  MenuItem,
  Typography,
} from "@mui/material"
import { PageLoader } from "@/components/ui/page-loader"
import { withCustomersAccess } from "@crm-src/components/page-permission-guard"

import {
  CreditCard,
  Users,
  MessageSquare,
  Activity,
  Contact,
  MoreHorizontal,
  Edit,
  X,
  RefreshCw,
  BookOpen
} from "lucide-react"

// Dashboard-side shared share components — relative imports (NOT `@/`, which the
// CRM tsconfig remaps to src/crm). See docs/crm.md + the share-to-chat spec.
import { buildEntityShareItems } from "../../../../components/share/entityShareItems"
import { ShareMenu } from "../../../../components/share/ShareMenu"
import { ShareToChatDialog } from "../../../../components/share/ShareToChatDialog"
import { customerShareSubject } from "../../../lib/shareSubject"

import { customersApi } from "@crm-src/lib/customers-api"
// Portal access controls live inline on each contact card now
// (see EditableContactInformationPanel + ContactPortalAccessButton).
import { dealsApi } from "@crm-src/lib/deals-api"
import type { DetailedCustomer, CustomerUpdateParams, CustomerSupportLevel, CustomerBusinessCategory } from "@crm-src/types/customer"
// Financial wall: route money through the one choke point. `useMoneyFormatter`
// yields a `(amount, options?) => string` formatter that redacts to "—" without
// financial access; `useFinancialAccess` drives hiding the whole Billing tab.
import { useFinancialAccess, useMoneyFormatter } from "@crm-src/components/money"

// Import panel components
import { FileManagerPanel } from "@crm-src/components/file-manager-panel"
import { NotesPanel } from "@crm-src/components/notes-panel"
import { ActivityLogPanel } from "@crm-src/components/activity-log-panel"
import { DealsInvoicesPanel } from "@crm-src/components/deals-invoices-panel"
import { CustomerFormModal } from "@crm-src/components/customer-form-modal"
import { EditAgreementModal } from "@crm-src/components/edit-agreement-modal"
import { DeleteCustomerModal } from "@crm-src/components/delete-customer-modal"
import { AddInvoiceModal } from "@crm-src/components/add-invoice-modal"
import { EditDealModal } from "@crm-src/components/edit-deal-modal"
import { EditableContactInformationPanel } from "@crm-src/components/editable-contact-information-panel"
import { TimezoneClock } from "@crm-src/components/timezone-clock"
import { ConvertToProspectModal } from "@crm-src/components/convert-to-prospect-modal"
import { CreateRecurringScheduleModal } from "@crm-src/components/create-recurring-schedule-modal"
import { EditRenewalDateModal } from "@crm-src/components/edit-renewal-date-modal"
import { invoiceSchedulesApi } from "@crm-src/lib/invoice-schedules-api"
import { DetailTabs } from "@crm-src/components/detail-tabs"
import { CustomerOverviewTab } from "@crm-src/components/customer-overview-tab"
import { useTabParam } from "@crm-src/hooks/useTabParam"
import usePermissions from "@crm-src/hooks/usePermissions"

import type { Deal, ScheduledInvoiceInfo, RecurringScheduleInfo } from "@crm-src/components/deals-invoices-panel"
import type { DetailTab } from "@crm-src/components/detail-tabs"
import { BackButton } from "@/components/ui/back-button"
// RESELLER PORT (2026-09-30): the Documentation tab is this portal's, not the
// source's — a reseller choosing which of the documents IT can read this
// customer sees on the support portal. Portal-owned and typechecked, outside
// this tree; see src/crm/README.md → CustomersDetailPage row.
import {
  CustomerDocumentationTab,
  useCanSeeCustomerDocumentation,
} from "@/pages/portal/customer-documentation/CustomerDocumentationTab"

const CUSTOMER_TABS: DetailTab[] = [
  // Overview now shows Customer Information + Contacts side by side, so
  // there is no separate "Contacts" tab.
  { id: "overview", label: "Overview", icon: Users },
  // Financial wall: "Billing" is a money-only tab (deals/invoices/totals).
  // It is filtered out of the visible tab set — and its panel skipped —
  // for a viewer without financial access (see `visibleTabs` below).
  { id: "billing", label: "Billing", icon: CreditCard },
  { id: "activity", label: "Activity", icon: Activity },
  // Notes + Files share one tab, side by side (no separate Files tab).
  { id: "notes", label: "Notes & Files", icon: MessageSquare },
  // RESELLER PORT: portal-owned tab, filtered out below without
  // customers:view + documentation:view (the keys the BFF gates its read on).
  { id: "documentation", label: "Documentation", icon: BookOpen },
]

function CustomerDetailsPage() {
  const params = useParams()
  const navigate = useNavigate()
  const { toast } = useToast()
  const { isAdmin } = usePermissions()
  const customerId = params.id as string

  // Financial wall: drop the money-only "Billing" tab (and its panel) entirely
  // for a viewer without financial access. Passing the gated id list to
  // useTabParam also means a direct `?tab=billing` deep-link falls back to
  // Overview rather than rendering the hidden panel.
  const hasFinancialAccess = useFinancialAccess()
  const formatCurrency = useMoneyFormatter()
  const canSeeDocumentation = useCanSeeCustomerDocumentation()
  const visibleTabs = CUSTOMER_TABS.filter(
    (tab) =>
      (hasFinancialAccess || tab.id !== "billing") &&
      (canSeeDocumentation || tab.id !== "documentation"),
  )
  const visibleTabIds = visibleTabs.map((tab) => tab.id)
  const [activeTab, setActiveTab] = useTabParam(visibleTabIds, "overview")

  const [customer, setCustomer] = useState<DetailedCustomer | null>(null)
  const [loading, setLoading] = useState(true)

  // Actions menu anchor — replaces the shadcn DropdownMenu during the MUI 5
  // migration.
  const [actionsAnchor, setActionsAnchor] = useState<HTMLElement | null>(null)

  // Edit dialog states
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false)
  const [chatShareOpen, setChatShareOpen] = useState(false)
  const [isEditAgreementDialogOpen, setIsEditAgreementDialogOpen] = useState(false)
  
  // Delete dialog states
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false)
  
  // Add invoice modal states
  const [isAddInvoiceModalOpen, setIsAddInvoiceModalOpen] = useState(false)
  const [selectedDealForInvoice, setSelectedDealForInvoice] = useState<Deal | null>(null)
  
  // Create deal modal states
  const [isCreateDealDialogOpen, setIsCreateDealDialogOpen] = useState(false)
  
  // Convert to prospect modal states
  const [isConvertToProspectModalOpen, setIsConvertToProspectModalOpen] = useState(false)
  const [isConverting, setIsConverting] = useState(false)

  // Scheduling modal states
  const [isRecurringModalOpen, setIsRecurringModalOpen] = useState(false)
  const [selectedInvoiceForSchedule, setSelectedInvoiceForSchedule] = useState<any>(null)

  // Renewal date modal state
  const [isRenewalDateModalOpen, setIsRenewalDateModalOpen] = useState(false)
  const [selectedDealForRenewal, setSelectedDealForRenewal] = useState<{ id: string, date: string | null } | null>(null)
  const [isSendingReminder, setIsSendingReminder] = useState(false)

  // Scheduled invoices data
  const [nextScheduledInvoice, setNextScheduledInvoice] = useState<ScheduledInvoiceInfo | null>(null)
  const [recurringSchedules, setRecurringSchedules] = useState<RecurringScheduleInfo[]>([])

  // Deals will come from customer data

  // Load customer data
  const loadCustomer = async () => {
    try {
      setLoading(true)
      const response = await customersApi.getById(customerId)
      setCustomer(response.customer)
    } catch (error) {
      console.error('Error loading customer:', error)
      toast({
        title: "Error",
        description: "Failed to load customer details",
        variant: "destructive",
      })
    } finally {
      setLoading(false)
    }
  }

  // Load scheduled invoices for this customer
  const loadScheduledInvoices = async () => {
    try {
      // Get upcoming scheduled invoices for this customer
      const upcomingResponse = await invoiceSchedulesApi.getUpcoming({
        customer_id: customerId,
        limit: 5,
        days: 90
      })

      if (upcomingResponse.success && upcomingResponse.data && upcomingResponse.data.length > 0) {
        // Get the next scheduled invoice
        const next = upcomingResponse.data[0]
        setNextScheduledInvoice({
          id: next.id,
          scheduled_date: next.scheduled_date,
          status: next.status,
          recipient_email: next.recipient_email,
          source_invoice: next.source_invoice,
          invoice_schedule: next.invoice_schedule
        })
      } else {
        setNextScheduledInvoice(null)
      }

      // Get recurring schedules for this customer
      const schedulesResponse = await invoiceSchedulesApi.getAll({
        customer_id: customerId,
        status: 'active'
      })

      if (schedulesResponse.success && schedulesResponse.data) {
        setRecurringSchedules(schedulesResponse.data.map((s: any) => ({
          id: s.id,
          name: s.name,
          frequency: s.frequency,
          status: s.status,
          next_scheduled_at: s.next_scheduled_at,
          source_invoice: s.source_invoice
        })))
      }
    } catch (error) {
      console.error('Error loading scheduled invoices:', error)
      // Don't show error toast - this is supplementary data
    }
  }

  useEffect(() => {
    if (customerId) loadCustomer()
  }, [customerId, toast])

  // Financial wall: scheduled invoices are monetary. The Billing tab is hidden
  // for a viewer without `crm:financials`, and the fetch must be skipped too —
  // `loadScheduledInvoices` hits the financial `/scheduled_invoices` +
  // `/invoice_schedules` endpoints, which the wall 403s for a non-financial role
  // (e.g. marketing_manager). The error was swallowed here (line below), but the
  // forbidden request still fired on every customer open. Mirrors the prospect
  // Quotes gating.
  useEffect(() => {
    if (customerId && hasFinancialAccess) loadScheduledInvoices()
  }, [customerId, hasFinancialAccess])

  // Refresh customer data after updates
  const refreshCustomerData = async () => {
    try {
      const response = await customersApi.getById(customerId)
      setCustomer(response.customer)
    } catch (error) {
      console.error('❌ Error refreshing customer:', error)
      toast({
        title: "Error",
        description: "Failed to refresh customer data",
        variant: "destructive",
      })
    }
  }

  // Inline edit (Overview tab): assign salesperson / change business type in
  // place without opening the edit modal. Both throw on failure so the shared
  // <InlineEditField> surfaces the error toast; on success we refresh.
  // See docs/crm.md → "Inline editing".
  const handleInlineAssignSalespeople = async (userIds: string[]) => {
    await customersApi.bulkAssignSalespeople([customerId], userIds)
    await refreshCustomerData()
    toast({ title: "Saved", description: "Salesperson updated" })
  }

  const handleInlineChangeBusinessCategory = async (value: CustomerBusinessCategory) => {
    await customersApi.update(customerId, { business_category: value })
    await refreshCustomerData()
    toast({ title: "Saved", description: "Business type updated" })
  }

  const handleInlineChangeIndustry = async (value: string) => {
    await customersApi.update(customerId, { industry: value })
    await refreshCustomerData()
    toast({ title: "Saved", description: "Industry updated" })
  }

  // Handle opening edit dialog
  const handleEditCustomer = () => {
    setIsEditDialogOpen(true)
  }

  // Handle opening edit agreement dialog
  const handleEditAgreement = () => {
    setIsEditAgreementDialogOpen(true)
  }

  // Handle customer update from modal
  const handleCustomerUpdated = async (updatedCustomer: DetailedCustomer) => {
    // Refresh complete customer data to ensure deals and other related data are included
    await refreshCustomerData()
  }

  // Handle agreement update from modal
  const handleAgreementUpdated = async (updatedCustomer: DetailedCustomer) => {
    // Refresh complete customer data to ensure deals and other related data are included
    await refreshCustomerData()
  }

  // Handle customer deletion
  const handleDeleteCustomer = () => {
    setIsDeleteDialogOpen(true)
  }

  // Handle convert to prospect - show confirmation modal
  const handleConvertToProspect = () => {
    setIsConvertToProspectModalOpen(true)
  }

  // Handle setting up recurring invoices
  const handleSetupRecurring = () => {
    // Get the most recent invoice for this customer to use as template
    const invoices = customer?.customer_deals?.[0]?.invoices || []
    const mostRecentInvoice = invoices[0]

    if (mostRecentInvoice) {
      setSelectedInvoiceForSchedule({
        id: mostRecentInvoice.id,
        invoice_number: mostRecentInvoice.number,
        total_amount: mostRecentInvoice.total_amount,
        customer_id: customerId,
        customer_name: customer?.full_name,
        customer_email: customer?.email
      })
      setIsRecurringModalOpen(true)
    } else {
      // No existing invoice - prompt to create one first
      toast({
        title: "No Invoice Available",
        description: "Please create an invoice first, then you can set up recurring billing.",
        variant: "default",
      })
      navigate(`/crm/invoices/generate?customerId=${customerId}`)
    }
  }

  // Handle viewing a scheduled invoice
  const handleViewScheduledInvoice = (scheduledId: string) => {
    navigate(`/crm/invoices/scheduled/${scheduledId}`)
  }

  // Handle viewing all schedules for this customer
  const handleViewAllSchedules = () => {
    navigate(`/crm/invoices/schedules?customer_id=${customerId}`)
  }

  // Handle scheduling success - refresh data
  const handleSchedulingSuccess = () => {
    loadScheduledInvoices()
    toast({
      title: "Success",
      description: "Invoice scheduling updated",
    })
  }

  // Handle editing AMC renewal date
  const handleEditRenewalDate = (dealId: string, currentDate: string | null) => {
    setSelectedDealForRenewal({ id: dealId, date: currentDate })
    setIsRenewalDateModalOpen(true)
  }

  // Handle saving renewal date
  const handleSaveRenewalDate = async (dealId: string, renewalDate: string | null) => {
    try {
      await dealsApi.updateDeal(dealId, { renewal_date: renewalDate })
      toast({
        title: "Success",
        description: renewalDate
          ? "AMC renewal date updated. A reminder task will be created automatically."
          : "AMC renewal date cleared.",
      })
      // Refresh customer data to reflect changes
      await refreshCustomerData()
    } catch (error: any) {
      toast({
        title: "Error",
        description: error.message || "Failed to update renewal date",
        variant: "destructive",
      })
      throw error
    }
  }

  // Handle sending renewal reminder manually
  const handleSendRenewalReminder = async (dealId: string) => {
    try {
      setIsSendingReminder(true)
      const result = await dealsApi.sendRenewalReminder(dealId)
      toast({
        title: "Reminder Sent",
        description: `${result.message}${result.recipients ? ` (${result.recipients.join(', ')})` : ''}`,
      })
    } catch (error: any) {
      const status = error.response?.status
      const errorMessage = error.response?.data?.error

      if (status === 429) {
        // Rate limited - reminder already sent recently
        toast({
          title: "Already Sent",
          description: errorMessage || "Reminder was already sent recently. Please wait before sending again.",
        })
      } else {
        toast({
          title: "Error",
          description: errorMessage || error.message || "Failed to send reminder",
          variant: "destructive",
        })
      }
    } finally {
      setIsSendingReminder(false)
    }
  }

  // Handle confirmed conversion to prospect
  const handleConfirmConvertToProspect = async () => {
    try {
      setIsConverting(true)
      const response = await customersApi.convertToProspect(customerId, { force_delete_agreements: true })
      
      toast({
        title: "Success",
        description: response.message || "Customer converted to prospect successfully",
      })
      
      // Close modal and redirect to prospects page
      setIsConvertToProspectModalOpen(false)
      navigate('/crm/prospects')
    } catch (error: any) {
      console.error('Error converting customer to prospect:', error)
      
      const errorData = error?.response?.data
      const errorMessage = errorData?.error || errorData?.errors?.[0] || 'Failed to convert customer to prospect'
      
      toast({
        title: "Error",
        description: errorMessage,
        variant: "destructive",
      })
    } finally {
      setIsConverting(false)
    }
  }

  // Handle navigation after successful deletion
  const handleNavigateToList = () => {
    navigate('/crm/customers')
  }

  // Handle add invoice
  const handleAddInvoice = (dealId: string) => {
    // Find the deal to get its details
    const deal = customer?.customer_deals?.find(d => d.id === dealId)
    if (deal) {
      setSelectedDealForInvoice(deal)
      setIsAddInvoiceModalOpen(true)
    }
  }

  // Handle invoice created
  const handleInvoiceCreated = (invoice: any) => {
    // Refresh customer data to show the new invoice
    refreshCustomerData()
  }

  // Handle create new deal
  const handleCreateDeal = () => {
    setIsCreateDealDialogOpen(true)
  }

  // Handle deal creation
  const handleDealCreated = async (dealData: any) => {
    try {
      
      // Prepare deal data for API  
      const createDealData = {
        customer_id: customerId,
        title: dealData.name,
        amount: dealData.amount,
        equipment_price: dealData.amount, // Set both amount and equipment_price
        maintenance_percentage: dealData.maintenance_percentage,
        start_date: dealData.start_date,
        end_date: dealData.end_date, // Map end_date if provided
        ...(dealData.renewal_date && { renewal_date: dealData.renewal_date }), // ✅ FIXED: Send renewal_date correctly
        deal_type: 'simple_purchase' // Default deal type
      }

      
      // Create the deal
      const result = await dealsApi.createDeal(createDealData)
      
      // Refresh data - EditDealModal handles modal closing and success message
      await refreshCustomerData()
    } catch (error: any) {
      console.error('Error creating deal:', error)
      toast({
        title: "Error",
        description: error.response?.data?.errors?.join(', ') || error.message || "Failed to create deal",
        variant: "destructive"
      })
      throw error // Re-throw so modal can handle loading state
    }
  }


  if (loading) {
    return <PageLoader />
  }

  if (!customer) {
    return (
      <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", py: 6 }}>
        <Typography component="h2" sx={{ fontSize: "1.25rem", fontWeight: 600, color: "text.primary", mb: 1 }}>Customer not found</Typography>
        <Typography sx={{ color: "text.secondary", mb: 2 }}>The customer you're looking for doesn't exist or has been removed.</Typography>
        <BackButton label="Go Back" />
      </Box>
    )
  }

  // Format support level for display
  const formatSupportLevel = (supportLevel: CustomerSupportLevel) => {
    switch (supportLevel) {
      case 'none':
        return 'NONE'
      case 'weekdays':
        return 'WEEK DAYS'
      case '24_7':
        return '24/7'
      default:
        return 'NONE'
    }
  }

  // Get support level badge color
  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
      {/* Header: back + record-level actions */}
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
        <BackButton />
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <ShareMenu
            items={buildEntityShareItems({
              subject: customerShareSubject(customer),
              onShareToChat: () => setChatShareOpen(true),
              notify: (m) => toast(m),
            })}
          />
          <Button variant="outlined" color="inherit" size="small" startIcon={<MoreHorizontal size={16} />} onClick={(e) => setActionsAnchor(e.currentTarget)}>
            Actions
          </Button>
        </Box>
        <Menu
          anchorEl={actionsAnchor}
          open={Boolean(actionsAnchor)}
          onClose={() => setActionsAnchor(null)}
          anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
          transformOrigin={{ vertical: "top", horizontal: "right" }}
        >
          <ListSubheader sx={{ lineHeight: 2, bgcolor: "transparent" }}>Customer Actions</ListSubheader>
          <MenuItem onClick={() => { setActionsAnchor(null); handleEditCustomer(); }}>
            <Box sx={{ mr: 1, display: "flex" }}><Edit size={16} /></Box>
            Edit Customer
          </MenuItem>
          <MenuItem onClick={() => { setActionsAnchor(null); handleConvertToProspect(); }}>
            <Box sx={{ mr: 1, display: "flex" }}><RefreshCw size={16} /></Box>
            Convert to Prospect
          </MenuItem>
          <Divider />
          <MenuItem
            sx={{ color: "error.main" }}
            onClick={() => { setActionsAnchor(null); handleDeleteCustomer(); }}
          >
            <Box sx={{ mr: 1, display: "flex" }}><X size={16} /></Box>
            Delete Customer
          </MenuItem>
        </Menu>
      </Box>

      {/* Identity */}
      <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
        <Avatar
          src={`https://api.dicebear.com/7.x/initials/svg?seed=${customer.company_name || customer.full_name}`}
          sx={{ width: 56, height: 56 }}
        >
          {customer.company_name
            ? customer.company_name.split(' ').map(word => word[0]).join('').substring(0, 2).toUpperCase()
            : `${customer.first_name?.[0] || 'U'}${customer.last_name?.[0] || 'N'}`}
        </Avatar>
        <Box>
          <Typography component="h2" sx={{ fontSize: "1.5rem", fontWeight: 700, color: "text.primary" }}>{customer.company_name || 'Unknown Company'}</Typography>
          <Box component="span" sx={{ color: "text.secondary", fontSize: "0.875rem", bgcolor: "action.hover", px: 1, py: 0.5, borderRadius: 1, display: "inline-block", mt: 0.5 }}>{customer.customer_id || customer.id}</Box>
        </Box>
      </Box>

      {/* Section tabs (Billing filtered out without financial access) */}
      <DetailTabs
        tabs={visibleTabs}
        activeId={activeTab}
        onTabChange={setActiveTab}
        ariaLabel="Customer sections"
      />

      {/* Active tab content */}
      {activeTab === 'overview' && (
        <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", lg: "1fr 1fr" }, gap: 3, alignItems: "start" }}>
          <CustomerOverviewTab
            customer={customer}
            editable={isAdmin()}
            onAssignSalespeople={handleInlineAssignSalespeople}
            onChangeBusinessCategory={handleInlineChangeBusinessCategory}
            onChangeIndustry={handleInlineChangeIndustry}
          />
          <Card>
            <CardHeader
              title={
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <Contact size={20} />
                  <Typography component="span" sx={{ fontWeight: 600 }}>Contacts</Typography>
                </Box>
              }
            />
            <CardContent>
              <EditableContactInformationPanel
                data={customer}
                customerId={customer.id}
                onContactsUpdated={(contacts) => {
                  setCustomer(prev => prev ? { ...prev, customer_contacts: contacts } : null)
                }}
              />
            </CardContent>
          </Card>
        </Box>
      )}

      {/* Financial wall: only render the money-bearing Billing panel for a
          viewer with financial access (defense in depth — the tab is already
          unreachable without it). */}
      {activeTab === 'billing' && hasFinancialAccess && (
        <DealsInvoicesPanel
          deals={customer.customer_deals || []}
          customerName={customer.full_name}
          customerId={customer.id.toString()}
          customerEmail={customer.email}
          supportLevel={customer.support_level}
          slaFirstResponseMinutes={customer.effective_sla_first_response_minutes}
          slaResolutionMinutes={customer.effective_sla_resolution_minutes}
          reseller={customer.reseller}
          product={customer.product}
          release={customer.installed_release_version}
          features={customer.features}
          dealers={customer.dealers}
          nextScheduledInvoice={nextScheduledInvoice}
          recurringSchedules={recurringSchedules}
          formatCurrency={formatCurrency}
          onEditAgreement={handleEditAgreement}
          onCreateAgreement={handleCreateDeal}
          onSetupRecurring={handleSetupRecurring}
          onViewScheduledInvoice={handleViewScheduledInvoice}
          onViewAllSchedules={handleViewAllSchedules}
          onEditRenewalDate={handleEditRenewalDate}
          onSendRenewalReminder={handleSendRenewalReminder}
          isSendingReminder={isSendingReminder}
          // Invoice/quote mutations (delete / download / mark paid|unpaid) now
          // live inside <CustomerInvoicesTable> (self-contained via invoicesApi);
          // onRefresh keeps the Agreement card's counts in sync after a change.
          onRefresh={refreshCustomerData}
        />
      )}

      {activeTab === 'activity' && (
        <ActivityLogPanel
          customerId={customerId}
          customerName={customer.full_name}
          showScheduleButton={true}
          autoRefresh={true}
        />
      )}

      {activeTab === 'notes' && (
        <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", lg: "1fr 1fr" }, gap: 3, alignItems: "start" }}>
          <NotesPanel
            customerId={customer.id}
            currentUserName="Current User"
          />
          <FileManagerPanel
            customerId={customer.id}
          />
        </Box>
      )}

      {/* RESELLER PORT: portal-owned (see the import). The CRM customer id is
          what portal-api resolves the support-portal organisation by. */}
      {activeTab === 'documentation' && canSeeDocumentation && (
        <CustomerDocumentationTab customerId={customerId} />
      )}

      {/* Edit Customer Modal */}
      <CustomerFormModal
        isOpen={isEditDialogOpen}
        onClose={() => setIsEditDialogOpen(false)}
        customer={customer}
        onSuccess={handleCustomerUpdated}
      />

      {/* Edit Agreement Modal */}
      <EditAgreementModal
        isOpen={isEditAgreementDialogOpen}
        onClose={() => setIsEditAgreementDialogOpen(false)}
        customer={customer}
        onAgreementUpdated={handleAgreementUpdated}
      />

      {/* Delete Customer Modal */}
      <DeleteCustomerModal
        isOpen={isDeleteDialogOpen}
        onClose={() => setIsDeleteDialogOpen(false)}
        customer={customer}
        onNavigateToList={handleNavigateToList}
      />

      {/* Add Invoice Modal */}
      <AddInvoiceModal
        isOpen={isAddInvoiceModalOpen}
        onClose={() => setIsAddInvoiceModalOpen(false)}
        deal={selectedDealForInvoice}
        customerId={customerId}
        onInvoiceCreated={handleInvoiceCreated}
      />

      {/* Create Deal Modal */}
      <EditDealModal
        open={isCreateDealDialogOpen}
        onOpenChange={setIsCreateDealDialogOpen}
        customerName={customer?.full_name}
        companyName={customer?.company_name}
        onSave={handleDealCreated}
      />

      {/* Convert to Prospect Modal */}
      <ConvertToProspectModal
        isOpen={isConvertToProspectModalOpen}
        onClose={() => setIsConvertToProspectModalOpen(false)}
        onConfirm={handleConfirmConvertToProspect}
        customerName={customer?.full_name || customer?.company_name || 'this customer'}
        agreementCount={customer?.customer_deals?.length || 0}
        invoiceCount={customer?.customer_deals?.reduce((total, deal) => total + (deal.invoices?.length || 0), 0) || 0}
        isLoading={isConverting}
      />

      {/* Create Recurring Schedule Modal */}
      <CreateRecurringScheduleModal
        isOpen={isRecurringModalOpen}
        onClose={() => setIsRecurringModalOpen(false)}
        onSuccess={handleSchedulingSuccess}
        invoice={selectedInvoiceForSchedule}
        customerId={customerId}
        customerName={customer?.full_name}
        customerEmail={customer?.email}
      />

      {/* Edit Renewal Date Modal */}
      <EditRenewalDateModal
        isOpen={isRenewalDateModalOpen}
        onClose={() => {
          setIsRenewalDateModalOpen(false)
          setSelectedDealForRenewal(null)
        }}
        onSave={handleSaveRenewalDate}
        dealId={selectedDealForRenewal?.id || null}
        currentDate={selectedDealForRenewal?.date || null}
        customerName={customer?.full_name}
      />

      {/* Share this customer into the internal staff chat */}
      <ShareToChatDialog
        open={chatShareOpen}
        onClose={() => setChatShareOpen(false)}
        subject={customerShareSubject(customer)}
      />

    </Box>
  )
}

export default withCustomersAccess(CustomerDetailsPage)
